import React, { RefObject, useRef } from 'react';
import classNames from 'classnames';

import ProductList, { PropsType as ProductListPropsType } from '../ProductList';
import { HTMLZone } from '../HTMLZone/HTMLZone';
import { useRatioCalculator } from './useRatioCalculator';
import {
  displayBigProduct,
  displayMiddleBannerWithBigProducts,
} from 'services/listing/merchandisingDisplay';
import {
  BigProductsType,
  HTMLContentType,
  ProductType,
} from 'types/ProductListing/ProductListing';

import styles from './ProductListWithBigProducts.module.scss';

type PropsType = {
  bigProducts: BigProductsType;
  topProducts: ProductType[];
  middleBanners: HTMLContentType;
} & ProductListPropsType;

const BANNERS_COUNT = 3;
const BIG_PRODUCTS_DISPLAY = { 1: 11, 2: 27, 3: 46 };
const MIDDLE_BANNERS_DISPLAY = { 1: [17, 1], 2: [33, 2], 3: [49, 3] };

export default function ProductListWithBigProducts({
  products,
  bigProducts,
  topProducts,
  renderProduct,
  middleBanners,
  ...rest
}: PropsType): JSX.Element {
  const productCount = topProducts.length + products.length;
  const bigProductsKeys = Object.keys(bigProducts).map(Number);
  const containerRef = useRef<HTMLDivElement | null>(null);

  return (
    <ProductList
      ref={containerRef}
      products={products}
      renderProduct={renderProduct}
      {...rest}
    >
      {topProducts.map(product => renderProduct(product, styles.topProduct))}
      <RenderBigProducts
        bigProducts={bigProducts}
        bigProductsKeys={bigProductsKeys}
        productCount={productCount}
        renderProduct={renderProduct}
        containerRef={containerRef}
      />
      {[...Array(BANNERS_COUNT)].map((_, i) => {
        const index = i + 1;
        const htmlContent = middleBanners[index];
        return (
          <HTMLZone
            key={`banner-${index}`}
            htmlContent={htmlContent}
            className={classNames(styles.banner, styles[`banner-${index}`], {
              [styles.dNone]: !displayMiddleBannerWithBigProducts(
                index,
                productCount,
                bigProductsKeys,
                MIDDLE_BANNERS_DISPLAY
              ),
              [styles.vHidden]: !htmlContent,
            })}
          />
        );
      })}
    </ProductList>
  );
}

function RenderBigProducts({
  bigProducts,
  productCount,
  bigProductsKeys,
  renderProduct,
  containerRef,
}: {
  bigProducts: BigProductsType;
  productCount: number;
  bigProductsKeys: number[];
  renderProduct: ProductListPropsType['renderProduct'];
  containerRef: RefObject<HTMLDivElement>;
}) {
  const ratio = useRatioCalculator(containerRef);

  return (
    <>
      {Object.entries(bigProducts).map(([key, product]) =>
        renderProduct(
          product,
          classNames(styles[`bigProduct-${key}`], {
            [styles.dNone]: !displayBigProduct(
              +key,
              productCount,
              bigProductsKeys,
              BIG_PRODUCTS_DISPLAY
            ),
          }),
          ratio
        )
      )}
    </>
  );
}
